"use client";

import { Card } from "@heroui/card";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Gem, Trophy } from "lucide-react";
import { QuestLogo } from "@/components/quest-logo";

type ValueType = "token" | "usd" | "generic";
type RecurringPeriod = "daily" | "weekly" | undefined;

interface TierInfo {
  type: ValueType;
  recurring?: RecurringPeriod;
  tokenInfo?: {
    image: string;
    symbol: string;
  };
}

interface TiersTableProps {
  tiers: Record<string, string>;
  tierInfo: TierInfo;
}

const formatValue = (value: string, valueType: ValueType) => {
  const numValue = Number(value);
  switch (valueType) {
    case "usd":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(numValue);
    case "token":
    case "generic":
    default:
      return numValue.toLocaleString("en-US", {
        maximumFractionDigits: 7,
      });
  }
};

export const TiersTable = ({ tiers, tierInfo }: TiersTableProps) => {
  const [hoveredTier, setHoveredTier] = useState<number | null>(null);

  // Convert the record format to array format and sort by requirement
  const tiersArray = Object.entries(tiers)
    .map(([requirement, points]) => ({
      requirement,
      points,
    }))
    .sort((a, b) => Number(a.requirement) - Number(b.requirement));

  return (
    <div className="w-full mb-6">
      <Card className="relative overflow-hidden bg-yiedl-gray-900 border border-yiedl-primary/20 rounded-xl p-4 shadow-lg">
        {/* Decorative background */}
        <div className="absolute inset-0">
          {/* Glow effects */}
          <div className="absolute -top-16 -left-16 w-36 h-36 bg-yiedl-primary/5 rounded-full blur-2xl" />
          <div className="absolute -bottom-16 -right-16 w-36 h-36 bg-yiedl-primary/5 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10">
          {/* Header section */}
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h4 className="text-base font-semibold text-white">
                Reward Tiers
              </h4>
            </div>
          </div>

          {/* Tiers section */}
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-yiedl-primary/20 scrollbar-track-transparent">
            {tiersArray.map((tier, index) => {
              const isHovered = hoveredTier === index;

              return (
                <div
                  key={index}
                  className={cn(
                    "relative pl-7 pr-3 py-2.5 flex items-center justify-between rounded-lg transition-all duration-200 ease-out",
                    isHovered
                      ? "bg-yiedl-primary/15"
                      : "bg-yiedl-gray-800/50 hover:bg-yiedl-primary/10",
                  )}
                  onMouseEnter={() => setHoveredTier(index)}
                  onMouseLeave={() => setHoveredTier(null)}
                >
                  {/* Dot indicator */}
                  <div className={cn(
                    "absolute left-2.5 w-2 h-2 rounded-full transition-all duration-200",
                    isHovered ? "bg-yiedl-primary scale-110" : "bg-yiedl-primary/60"
                  )} />

                  {/* Tier requirement */}
                  <div className="mr-2 flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span
                        className={cn(
                          "text-sm font-semibold transition-colors",
                          isHovered ? "text-yiedl-primary" : "text-white"
                        )}
                      >
                        {tierInfo.type === "generic"
                          ? tier.requirement
                          : formatValue(tier.requirement, tierInfo.type)}
                      </span>
                      {tierInfo.type === "token" && tierInfo.tokenInfo && (
                        <span className="text-xs text-gray-400">
                          {tierInfo.tokenInfo.symbol}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Points badge */}
                  <div
                    className={cn(
                      "flex items-center gap-1.5 rounded-full px-2.5 py-1 transition-all duration-200 ease-out flex-shrink-0",
                      isHovered
                        ? "bg-yiedl-primary text-black"
                        : "bg-yiedl-primary/20 text-yiedl-primary"
                    )}
                  >
                    <span className="text-xs font-semibold">
                      +{Number(tier.points).toLocaleString()}
                    </span>
                    <QuestLogo
                      size={14}
                      className={cn(
                        "shrink-0",
                        isHovered && "text-black"
                      )}
                    />
                  </div>
                </div>
              );
            })}
          </div>       
        </div>
      </Card>
    </div>
  );
};
